import clsx from 'clsx';
import { Heading, Text } from '@mysten/ui';
import { useMemo, useEffect, useState } from 'react';

import type { ClobTradeRow } from '@cpc/tx';
import { useT } from '~/i18n/LocaleContext';
import { Card } from '~/ui/Card';

import {
	DEPTH_ROW_PX,
	RECENT_TRADE_VISIBLE_ROWS,
	formatLastTradePrice,
	formatTradeTimeColumn,
	recentTradesPaneHeightPx,
	resolveTradeDisplaySide,
	tradeCls,
	tradeFillSideCls,
} from './tradeTheme';
import { formatClobQtyLots } from '~/config/clobConfig';

export { RECENT_TRADE_VISIBLE_ROWS };

/** 三列自适应窄栏：价格左 / 数量中 / 时间右 */
const ROW_GRID =
	'grid w-full grid-cols-[minmax(0,1fr)_3.5rem_4rem] items-center gap-x-1';

export function TradeRecentTrades({
	rows,
	baseSymbol = 'DGC',
}: {
	rows: ClobTradeRow[];
	viewerAddress?: string;
	baseSymbol?: string;
}) {
	const t = useT();
	const visible = rows.slice(0, RECENT_TRADE_VISIBLE_ROWS);
	const paneH = recentTradesPaneHeightPx();

	// 1. H5 响应式状态检测：实时获取当前屏幕视口宽度
	const [isMobile, setIsMobile] = useState(false);
	useEffect(() => {
		setIsMobile(window.innerWidth < 768);
		const handleResize = () => setIsMobile(window.innerWidth < 768);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return (
		/* 2. 移动端优化：
		   - growOnHover={!isMobile}：仅在 PC 端开启悬浮放大效果，避免 H5 端手指滑动时误触。
		   - className="max-md:h-auto"：确保在移动端外壳不会被 PC 的某些满高逻辑强制拉长。
		*/
		<Card bg="dark" spacing="sm" height="full" growOnHover={!isMobile} className="max-md:h-auto">
			<div className="flex w-full min-w-0 flex-col overflow-hidden">
				<div className="mb-1.5 flex items-baseline justify-between gap-2">
					<Heading variant="heading4/semibold" color="dark" className="text-[15px] md:text-base">
						{t('trade.recentTrades')}
					</Heading>
					<Text variant="caption/semibold" color="steel-dark" className="shrink-0 tabular-nums">
						{visible.length}/{RECENT_TRADE_VISIBLE_ROWS}
					</Text>
				</div>

				<div className={clsx(ROW_GRID, 'mb-1 shrink-0 border-b border-gray-45 pb-1.5')}>
					<Text variant="caption/semibold" color="dark" className="text-left">
						{t('common.price')}
					</Text>
					<Text variant="caption/semibold" color="dark" className="text-center">
						{t('common.quantity')}({baseSymbol})
					</Text>
					<Text variant="caption/semibold" color="dark" className="text-right">
						{t('common.time')}
					</Text>
				</div>

				{/* 3. 【高度调优核心区】：
				   - PC 端 (isMobile 为 false): 严格沿用原有的 paneH 固定高度。
				   - H5 端 (isMobile 为 true): 整体高度定死在 180px（这个数值在手机端很精致，包含约 8 条数据），
				     同时加入 overflow-y-auto 开通原生丝滑滚动，以便用户在小屏上也能滑着看完整数据。
				*/}
				<div
					className={clsx("w-full min-w-0 shrink-0", isMobile ? "overflow-y-auto no-scrollbar" : "overflow-hidden")}
					style={
						isMobile
							? { height: '180px', minHeight: '180px' }
							: { height: paneH, minHeight: paneH }
					}
				>
					{visible.length === 0 ? (
						<div className="flex h-full items-center justify-center">
							<Text variant="body/medium" color="dark">
								{t('trade.noTrades')}
							</Text>
						</div>
					) : (
						visible.map((row, i) => {
							const displaySide = resolveTradeDisplaySide(row, visible[i + 1]);
							const sideCls = tradeFillSideCls(displaySide);
							return (
							<div
								key={`${row.digest}-${row.priceTicks}-${row.qtyLots}-${row.timestampMs}`}
								className={clsx(ROW_GRID, tradeCls.cosure)}
								style={{ height: DEPTH_ROW_PX }}
							>
								<span
									className={clsx(
										'min-w-0 truncate text-left text-captionSmall tabular-nums',
										sideCls,
									)}
									title={formatLastTradePrice(row.dataPerDgc)}
								>
									{formatLastTradePrice(row.dataPerDgc)}
								</span>
								<span
									className={clsx(
										'truncate text-center text-captionSmall tabular-nums',
										sideCls,
									)}
									title={formatClobQtyLots(row.qtyLots, row.timestampMs)}
								>
									{formatClobQtyLots(row.qtyLots, row.timestampMs)}
								</span>
								<span
									className={clsx(
										'truncate text-right text-captionSmall tabular-nums',
										tradeCls.textMuted,
									)}
									title={formatTradeTimeColumn(row.timestampMs)}
								>
									{formatTradeTimeColumn(row.timestampMs)}
								</span>
							</div>
							);
						})
					)}
				</div>
			</div>
		</Card>
	);
}